/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import MessageSettingsService from 'cio/services/message-settings';
import { getTimeFrameUnitsForMaxUnit, findRoundUnit } from 'cio/utils/round-timeframe-units';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import {
  isAutoRetryWindowCorrect,
  MAX_MESSAGE_RETRY_WINDOW_SECS,
  DEFAULT_MESSAGE_RETRY_WINDOW_SECS,
} from 'cio/utils/message-auto-retry';

enum AutoRetrySettingsOption {
  useWorkspaceSettings = 'use_workspace_settings',
  doNotRetryMessages = 'do_not_retry_messages',
  setCustomWindow = 'set_custom_window',
}

interface MessagingLimitWorkflowSettingsWithRetryWindowArgs {
  disabled: boolean;
  useMessageLimits: boolean;
  useWorkspaceMessageLimitsRetry: boolean;
  messageLimitsRetryWindowSecs: number;
  onUseMessageLimitsChange: (useMessageLimits: boolean) => unknown;
  onUseWorkspaceMessageLimitsRetryChange: (useWorkspaceMessageLimitsRetry: boolean) => unknown;
  onMessageLimitsRetryWindowSecsChange: (messageLimitsRetryWindowSecs: number) => unknown;
  onMessageLimitsRetryWindowSecsValidate: (valid: boolean) => unknown;
  hasReadOnlyPermission: boolean;
}

export default class MessagingLimitWorkflowSettingsWithRetryWindow extends Component<MessagingLimitWorkflowSettingsWithRetryWindowArgs> {
  @service messageSettings!: MessageSettingsService;

  readonly maxMessageRetryWindowSecs = MAX_MESSAGE_RETRY_WINDOW_SECS;

  readonly autoRetrySettingsOptions = [
    {
      label: 'Use workspace settings',
      value: AutoRetrySettingsOption.useWorkspaceSettings,
    },
    {
      label: "Don't retry messages in this campaign",
      value: AutoRetrySettingsOption.doNotRetryMessages,
    },
    {
      label: 'Set a custom window',
      value: AutoRetrySettingsOption.setCustomWindow,
    },
  ];

  get autoRetrySettingValue() {
    if (this.args.useWorkspaceMessageLimitsRetry) {
      return AutoRetrySettingsOption.useWorkspaceSettings;
    }

    if (this.args.messageLimitsRetryWindowSecs > 0) {
      return AutoRetrySettingsOption.setCustomWindow;
    }

    return AutoRetrySettingsOption.doNotRetryMessages;
  }

  get readonly() {
    return this.args.disabled || this.args.hasReadOnlyPermission;
  }

  get isLimitingEnabled() {
    return this.messageSettings.isMessagingLimitEnabled;
  }

  get workspaceRetryWindowSecs() {
    return this.messageSettings.messageLimit?.retryWindowSecs ?? 0;
  }

  get workspaceRetryWindowValueText() {
    let unit = findRoundUnit(this.workspaceRetryWindowSecs, getTimeFrameUnitsForMaxUnit('hour'));
    let timeframe = Math.ceil(this.workspaceRetryWindowSecs / unit.seconds);

    return `${formatNumberAndPluralize([timeframe, unit.value])}`;
  }

  @action
  onAutoRetrySettingChange({ value }: { label: string; value: string }) {
    this.args.onMessageLimitsRetryWindowSecsValidate(true);

    if (value === AutoRetrySettingsOption.useWorkspaceSettings) {
      this.args.onUseWorkspaceMessageLimitsRetryChange(true);
      return;
    }

    if (value === AutoRetrySettingsOption.doNotRetryMessages) {
      this.args.onUseWorkspaceMessageLimitsRetryChange(false);
      this.args.onMessageLimitsRetryWindowSecsChange(0);
      return;
    }

    if (value === AutoRetrySettingsOption.setCustomWindow) {
      this.args.onUseWorkspaceMessageLimitsRetryChange(false);
      let seconds = this.args.messageLimitsRetryWindowSecs;
      if (seconds === 0) {
        seconds = DEFAULT_MESSAGE_RETRY_WINDOW_SECS;
      }
      this.args.onMessageLimitsRetryWindowSecsChange(seconds);
    }
  }

  @action
  onMessageLimitsRetryWindowSecsChange(seconds: number) {
    let isCorrectValue = isAutoRetryWindowCorrect(seconds);
    if (isCorrectValue) {
      this.args.onMessageLimitsRetryWindowSecsChange(seconds);
    }
    this.args.onMessageLimitsRetryWindowSecsValidate(isCorrectValue);
  }
}
